import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { createUseStyles } from '../../../utils/styles';
import { useClasses } from '../../../utils/overrides';
import styles from './imageCropStyles';

const useStyles = createUseStyles(styles, 'ImageCrop');

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const MIN_SIZE = 20;

const ImageCrop = memo(
    ({ aspect, className, classes: classesProp, crop, onChange, onLoad, src }) => {
        const classes = useClasses(useStyles, classesProp);
        const imageRef = useRef(null);
        const stateRef = useRef({
            dragging: false,
            handle: null,
            startX: 0,
            startY: 0,
            startCrop: {},
        });
        const [active, setActive] = useState(false);

        const constrainCrop = useCallback(
            (c, imgW, imgH, handle) => {
                let { x, y, width, height } = c;

                // Ensure minimum size
                if (width < MIN_SIZE) {
                    if (handle?.includes('w')) x -= MIN_SIZE - width;
                    width = MIN_SIZE;
                }
                if (height < MIN_SIZE) {
                    if (handle?.includes('n')) y -= MIN_SIZE - height;
                    height = MIN_SIZE;
                }

                // Apply aspect ratio
                if (aspect) {
                    if (width / height > aspect) {
                        const newWidth = height * aspect;
                        if (handle?.includes('w')) x += width - newWidth;
                        width = newWidth;
                    } else {
                        const newHeight = width / aspect;
                        if (handle?.includes('n')) y += height - newHeight;
                        height = newHeight;
                    }
                }

                // Constrain to image bounds
                if (x < 0) {
                    if (!handle || handle.includes('w')) width += x;
                    x = 0;
                }
                if (y < 0) {
                    if (!handle || handle.includes('n')) height += y;
                    y = 0;
                }
                if (x + width > imgW) {
                    width = imgW - x;
                }
                if (y + height > imgH) {
                    height = imgH - y;
                }

                return { x, y, width, height, unit: 'px' };
            },
            [aspect],
        );

        const onMove = useCallback(
            (e) => {
                const { dragging, handle, startX, startY, startCrop } = stateRef.current;
                if (!dragging || !imageRef.current) return;

                const clientX = e.clientX ?? e.touches?.[0]?.clientX;
                const clientY = e.clientY ?? e.touches?.[0]?.clientY;
                const dx = clientX - startX;
                const dy = clientY - startY;
                const { width: imgW, height: imgH } = imageRef.current;

                let newCrop;
                if (!handle) {
                    newCrop = { ...startCrop, x: startCrop.x + dx, y: startCrop.y + dy };
                } else {
                    newCrop = { ...startCrop };
                    if (handle.includes('n')) {
                        newCrop.y = startCrop.y + dy;
                        newCrop.height = startCrop.height - dy;
                    }
                    if (handle.includes('s')) {
                        newCrop.height = startCrop.height + dy;
                    }
                    if (handle.includes('w')) {
                        newCrop.x = startCrop.x + dx;
                        newCrop.width = startCrop.width - dx;
                    }
                    if (handle.includes('e')) {
                        newCrop.width = startCrop.width + dx;
                    }
                }

                onChange(constrainCrop(newCrop, imgW, imgH, handle));
            },
            [onChange, constrainCrop],
        );

        const onUp = useCallback(() => {
            stateRef.current.dragging = false;
            setActive(false);
        }, []);

        const onDown = useCallback(
            (e, handle = null) => {
                e.preventDefault();
                e.stopPropagation();

                const clientX = e.clientX ?? e.touches?.[0]?.clientX;
                const clientY = e.clientY ?? e.touches?.[0]?.clientY;

                stateRef.current = {
                    dragging: true,
                    handle,
                    startX: clientX,
                    startY: clientY,
                    startCrop: { ...crop },
                };
                setActive(true);
            },
            [crop],
        );

        useEffect(() => {
            if (!active) return;

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.addEventListener('touchmove', onMove);
            document.addEventListener('touchend', onUp);

            return () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                document.removeEventListener('touchmove', onMove);
                document.removeEventListener('touchend', onUp);
            };
        }, [active, onMove, onUp]);

        const hasCrop = crop.width > 0 && crop.height > 0;

        return (
            <div className={classnames(classes.root, className)}>
                <img
                    ref={imageRef}
                    src={src}
                    alt=""
                    className={classes.image}
                    draggable={false}
                    onLoad={onLoad}
                />
                {hasCrop && (
                    <div
                        className={classes.selection}
                        style={{
                            top: crop.y,
                            left: crop.x,
                            width: crop.width,
                            height: crop.height,
                        }}
                        onMouseDown={onDown}
                        onTouchStart={onDown}
                    >
                        {HANDLES.map((h) => (
                            <div
                                key={h}
                                className={classnames(
                                    classes.handle,
                                    classes[`handle${h.charAt(0).toUpperCase()}${h.slice(1)}`],
                                )}
                                onMouseDown={(e) => onDown(e, h)}
                                onTouchStart={(e) => onDown(e, h)}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    },
);

ImageCrop.propTypes = {
    aspect: PropTypes.number,
    className: PropTypes.string,
    classes: PropTypes.object,
    crop: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
        unit: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    onLoad: PropTypes.func,
    src: PropTypes.string.isRequired,
};

export default ImageCrop;
